/**
 * Pure scoring and tie-break logic for matching a quiz completer onto a
 * character (see docs/scripted-chat-visual-matching-plan.md).
 *
 * Deliberately free of Firebase imports: `userService.tsx` owns the Firestore
 * plumbing (which pool to load for which `postQuizChatMode` arm, excluding
 * characters the user already chats with) and delegates the actual ranking
 * here, and `apps/scripts/characters/audit-onboarding-match-coverage.ts`
 * imports this module directly so the coverage audit runs the *same* code the
 * app does instead of a re-implementation that can drift.
 */

import type { Character, OnboardingUser } from "../types";

export type OnboardingAppearanceAnswers = {
  ethnicity?: string;
  hairColor?: string;
  figure?: string;
  breastSize?: string;
};

/** The subset of a character document the scorer reads. */
export type MatchableCharacter = Pick<
  Character,
  "id" | "ethnicity" | "hairColor" | "figure" | "breastSize" | "activeScriptId" | "photos"
>;

export const ETHNICITY_WEIGHT = 3;
export const HAIR_WEIGHT = 2;
export const BREAST_WEIGHT = 1;
export const FIGURE_WEIGHT = 0.5;

/** Maximum score a candidate can reach — every answered attribute matched exactly. */
export const MAX_MATCH_SCORE = ETHNICITY_WEIGHT + HAIR_WEIGHT + BREAST_WEIGHT + FIGURE_WEIGHT;

export const ETHNICITY_SIMILARITY: Record<string, Record<string, number>> = {
  caucasian: {
    caucasian: 1,
    latina: 0.6,
    middle_eastern: 0.6,
    asian: 0.4,
    black: 0,
  },
  latina: {
    latina: 1,
    caucasian: 0.6,
    middle_eastern: 0.7,
    asian: 0.3,
    black: 0,
  },
  middle_eastern: {
    middle_eastern: 1,
    latina: 0.7,
    caucasian: 0.6,
    asian: 0.4,
    black: 0,
  },
  asian: {
    asian: 1,
    middle_eastern: 0.4,
    latina: 0.3,
    caucasian: 0.4,
    black: 0,
  },
  black: {
    black: 1,
    caucasian: 0,
    latina: 0,
    middle_eastern: 0,
    asian: 0,
  },
};

export const FIGURE_ORDER = ["extra_skinny", "skinny", "curvy", "thick"];
export const BREAST_SIZE_ORDER = ["small", "medium", "large", "huge"];

/** Reads the appearance answers the scorer cares about off an onboarding document. */
export function extractAppearanceAnswers(
  answers: OnboardingUser["answers"] | undefined
): OnboardingAppearanceAnswers {
  if (!answers) {
    return {};
  }
  const pick = (key: string): string | undefined =>
    typeof answers[key] === "string" ? (answers[key] as string) : undefined;
  return {
    ethnicity: pick("ethnicity"),
    hairColor: pick("hairColor"),
    figure: pick("figure"),
    breastSize: pick("breastSize"),
  };
}

function scoreOrdered(
  desired: string | undefined,
  actual: string | undefined,
  order: string[]
): number {
  if (!desired || !actual) return 0;
  const desiredIndex = order.indexOf(desired);
  const actualIndex = order.indexOf(actual);
  if (desiredIndex === -1 || actualIndex === -1) return 0;
  const distance = Math.abs(desiredIndex - actualIndex);
  if (distance === 0) return 1;
  if (distance === 1) return 0.6;
  if (distance === 2) return 0.3;
  return 0;
}

function scoreEthnicity(desired: string | undefined, actual: string | undefined): number {
  if (!desired || !actual) return 0;
  return ETHNICITY_SIMILARITY[desired]?.[actual] ?? 0;
}

/**
 * How well one character matches the appearance answers. Higher is better;
 * `MAX_MATCH_SCORE` means every answered attribute matched exactly.
 * `hairColor: "no_preferences"` contributes nothing either way.
 */
export function scoreCharacterMatch(
  answers: OnboardingAppearanceAnswers,
  character: MatchableCharacter
): number {
  const shouldMatchHairColor = answers.hairColor && answers.hairColor !== "no_preferences";

  let score = 0;
  score += scoreEthnicity(answers.ethnicity, character.ethnicity) * ETHNICITY_WEIGHT;
  if (shouldMatchHairColor && character.hairColor === answers.hairColor) {
    score += HAIR_WEIGHT;
  }
  score += scoreOrdered(answers.breastSize, character.breastSize, BREAST_SIZE_ORDER) * BREAST_WEIGHT;
  score += scoreOrdered(answers.figure, character.figure, FIGURE_ORDER) * FIGURE_WEIGHT;
  return score;
}

/**
 * Returns every candidate that reaches the best score, in input order, plus
 * that score. Empty input yields an empty list and a score of -1.
 */
export function selectBestMatches<T extends MatchableCharacter>(
  answers: OnboardingAppearanceAnswers,
  candidates: T[]
): { bestScore: number; bestMatches: T[] } {
  let bestScore = -1;
  let bestMatches: T[] = [];

  for (const character of candidates) {
    const score = scoreCharacterMatch(answers, character);
    if (score > bestScore) {
      bestScore = score;
      bestMatches = [character];
    } else if (score === bestScore) {
      bestMatches.push(character);
    }
  }

  return { bestScore, bestMatches };
}

export function hasActiveScript(character: Pick<Character, "activeScriptId">): boolean {
  return typeof character.activeScriptId === "string" && character.activeScriptId.length > 0;
}

/**
 * Whether a character has at least one spicy photo that has not been removed
 * by moderation — the minimum a scripted scene needs to have anything to
 * unlock. Used to keep media-less characters out of the scripted matching
 * pool.
 */
export function hasSpicyPhoto(character: Pick<Character, "photos">): boolean {
  return (character.photos ?? []).some(
    (photo) => photo.isSpicy === true && photo.moderationStatus !== "removed"
  );
}

/**
 * FNV-1a (32-bit): a tiny, stable, non-cryptographic string hash. Used only to
 * spread tied onboarding matches across users, so the same input always maps to
 * the same bucket in every browser and across re-runs.
 */
export function hashStringToUint32(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * Tie-break for equally good onboarding matches.
 *
 * Picking e.g. the newest character collapses every tied user onto a single
 * girl — with the short flows several characters differ only in attributes the
 * quiz no longer asks about, so a large share of users would all be routed to
 * the same one. Instead, hash a stable per-user seed and index into the tied
 * candidates, which spreads users evenly while staying deterministic: the same
 * user re-running matching (retry, re-entering the flow) always lands on the
 * same character. The candidates are sorted by id first, because the pool comes
 * from a Firestore query whose order is not guaranteed.
 */
export function pickTiedCharacterId(tiedCharacterIds: string[], seed: string): string | null {
  if (tiedCharacterIds.length === 0) {
    return null;
  }

  const orderedIds = [...tiedCharacterIds].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  if (orderedIds.length === 1) {
    return orderedIds[0];
  }

  return orderedIds[hashStringToUint32(seed) % orderedIds.length];
}

export type SelectMatchedCharacterOptions = {
  /**
   * When true and the best-scoring set contains at least one character with an
   * `activeScriptId`, the tie-break only considers those. A pilot's
   * `scriptMediaMap` is hand-wired to its script's scene, so among equally good
   * looks it is the better scripted chat; a non-pilot is only chosen when it
   * is strictly closer to what the user asked for.
   */
  preferScripted?: boolean;
};

/**
 * The whole ranking in one call: score, keep the best, optionally prefer
 * pilots among them, then tie-break deterministically on `seed`.
 */
export function selectMatchedCharacterId(
  answers: OnboardingAppearanceAnswers,
  candidates: MatchableCharacter[],
  seed: string,
  options?: SelectMatchedCharacterOptions
): string | null {
  const { bestMatches } = selectBestMatches(answers, candidates);
  if (bestMatches.length === 0) {
    return null;
  }

  let tied = bestMatches;
  if (options?.preferScripted) {
    const scripted = bestMatches.filter(hasActiveScript);
    if (scripted.length > 0) {
      tied = scripted;
    }
  }

  return pickTiedCharacterId(
    tied.map((character) => character.id),
    seed
  );
}
